// Filtering and sorting for the admin analytics index. Kept pure so it can be
// unit tested without rendering the page.

export const TYPE_DEFAULT = 'default'
export const TYPE_CUSTOM = 'custom'
export const TYPE_ALL = 'all'

export const DEFAULT_FILTERS = {
  type: TYPE_DEFAULT,
  includeInactive: false,
  search: '',
  section: '',
  municipality: '',
  errorsOnly: false,
  orderBy: 'key',
  orderDirection: 'asc'
}

// "Default" analytics are the ThirdLine-authored catalogue (no owning
// municipality). Only the ones switched on (`is_core`) are shown unless
// `includeInactive` is set. "Custom" analytics are municipality-owned recipes.
const matchesType = (analytic, { type, includeInactive }) => {
  if (type === TYPE_CUSTOM) return analytic.custom
  if (type === TYPE_DEFAULT) return !analytic.custom && (includeInactive || analytic.is_core)
  return true
}

const matchesSearch = (analytic, search) => {
  if (!search) return true
  const term = search.toLowerCase()
  return [analytic.key, analytic.name, analytic.description, analytic.section_name, analytic.municipality_name]
    .some((value) => value?.toLowerCase().includes(term))
}

export const filterAnalytics = (analytics = [], filters = DEFAULT_FILTERS) =>
  analytics.filter(
    (analytic) =>
      matchesType(analytic, filters) &&
      matchesSearch(analytic, filters.search) &&
      (!filters.section || analytic.section_abbreviation === filters.section) &&
      (!filters.municipality || analytic.municipality_id === filters.municipality) &&
      (!filters.errorsOnly || analytic.error_count > 0)
  )

const NUMERIC_COLUMNS = ['municipality_count', 'flags_count', 'error_count']

export const sortAnalytics = (analytics, { orderBy, orderDirection }) => {
  const direction = orderDirection === 'desc' ? -1 : 1
  const numeric = NUMERIC_COLUMNS.includes(orderBy)

  return [...analytics].sort((a, b) => {
    if (numeric) return ((a[orderBy] || 0) - (b[orderBy] || 0)) * direction
    return String(a[orderBy] ?? '').localeCompare(String(b[orderBy] ?? ''), undefined, { numeric: true }) * direction
  })
}

// Owning municipalities of the custom analytics, for the municipality filter.
export const customMunicipalities = (analytics = []) => {
  const byId = new Map()
  analytics.forEach((analytic) => {
    if (analytic.custom && analytic.municipality_id) byId.set(analytic.municipality_id, analytic.municipality_name)
  })
  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
}

// URL search params <-> filter state, so the list keeps its filters when you
// come back from an analytic.
export const filtersFromSearchParams = (searchParams) => ({
  ...DEFAULT_FILTERS,
  type: [TYPE_DEFAULT, TYPE_CUSTOM, TYPE_ALL].includes(searchParams.get('type'))
    ? searchParams.get('type')
    : DEFAULT_FILTERS.type,
  includeInactive: searchParams.get('inactive') === '1',
  search: searchParams.get('search') || '',
  section: searchParams.get('section') || '',
  municipality: searchParams.get('municipality') || '',
  errorsOnly: searchParams.get('errors') === '1',
  orderBy: searchParams.get('order_by') || DEFAULT_FILTERS.orderBy,
  orderDirection: searchParams.get('order_direction') === 'desc' ? 'desc' : 'asc'
})

export const searchParamsFromFilters = (filters) => {
  const params = {}
  if (filters.type !== DEFAULT_FILTERS.type) params.type = filters.type
  if (filters.includeInactive) params.inactive = '1'
  if (filters.search) params.search = filters.search
  if (filters.section) params.section = filters.section
  if (filters.municipality) params.municipality = filters.municipality
  if (filters.errorsOnly) params.errors = '1'
  if (filters.orderBy !== DEFAULT_FILTERS.orderBy) params.order_by = filters.orderBy
  if (filters.orderDirection !== DEFAULT_FILTERS.orderDirection) params.order_direction = filters.orderDirection
  return params
}
